import { useId } from 'react'
import { twMerge } from 'tailwind-merge'
import { BadgePatternLayer, type BadgePatternId } from './badge-patterns'
import {
  BADGE_VIEW_BOX,
  getBadgeShapeSpec,
  getBadgeShapeTransform
} from './shape-registry'
import {
  BADGE_TONES,
  type BadgeDisplaySymbol,
  type BadgeShapeId,
  type BadgeToneId
} from './types'
import { getUntitledIconComponent } from './untitled-ui-icon-registry'
import { addOrdinalSuffix } from '../../utils/textFormatting'

interface BadgeDisplayProps {
  patternId?: BadgePatternId
  shapeId: BadgeShapeId
  symbol?: BadgeDisplaySymbol
  toneId?: BadgeToneId
  size?: number
  topAdornment?: 'crown'
  rank?: string | number
  label?: string
  className?: string
}

const SHADOW_ID_SUFFIX = 'badge-shadow'
const CLIP_ID_SUFFIX = 'badge-clip'
const INNER_RIM_SHADOW_ID_SUFFIX = 'badge-inner-rim-shadow'

const BADGE_CROWN_CONFIG: Record<
  BadgeShapeId,
  {
    topRatio: number
    widthRatio: number
    heightScale?: number
  }
> = {
  circle: { topRatio: -0.035, widthRatio: 0.38 },
  diamond: { topRatio: -0.02, widthRatio: 0.435, heightScale: 1.12 },
  hexagon: { topRatio: -0.08, widthRatio: 0.42 },
  roundedSquare: { topRatio: -0.04, widthRatio: 0.38 },
  rosette: { topRatio: -0.05, widthRatio: 0.455, heightScale: 1 },
  seal: { topRatio: -0.095, widthRatio: 0.42, heightScale: 1.4 },
  shield: { topRatio: -0.085, widthRatio: 0.4 }
}

const BADGE_CONTENT_CONFIG: Record<
  BadgeShapeId,
  {
    emojiScale: number
    iconScale: number
    offsetYRatio: number
  }
> = {
  circle: { emojiScale: 1.08, iconScale: 1.02, offsetYRatio: -0.01 },
  diamond: { emojiScale: 0.94, iconScale: 0.9, offsetYRatio: -0.015 },
  hexagon: { emojiScale: 1.02, iconScale: 1, offsetYRatio: -0.012 },
  roundedSquare: { emojiScale: 0.98, iconScale: 0.94, offsetYRatio: -0.006 },
  rosette: { emojiScale: 1, iconScale: 0.96, offsetYRatio: -0.018 },
  seal: { emojiScale: 1, iconScale: 0.96, offsetYRatio: -0.018 },
  shield: { emojiScale: 1.06, iconScale: 1.01, offsetYRatio: -0.07 }
}

function capitalizeFirstCharacter(value: string) {
  if (!value) {
    return value
  }

  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
}

function formatRankLabel(rank: string | number | undefined) {
  if (rank === undefined || rank === null || rank === '') {
    return undefined
  }

  const rankNumber = Number.parseInt(String(rank), 10)

  if (Number.isNaN(rankNumber)) {
    return undefined
  }

  return addOrdinalSuffix(rankNumber)
}

function renderShapeGeometry({
  geometry,
  props,
  transform
}: {
  geometry: ReturnType<typeof getBadgeShapeSpec>['fill']
  props?: Record<string, number | string>
  transform: string
}) {
  if (geometry.kind === 'circle') {
    return (
      <circle
        {...props}
        cx={geometry.cx}
        cy={geometry.cy}
        r={geometry.r}
        transform={transform}
      />
    )
  }

  if (geometry.kind === 'rect') {
    return (
      <rect
        {...props}
        height={geometry.height}
        rx={geometry.rx}
        width={geometry.width}
        x={geometry.x}
        y={geometry.y}
        transform={transform}
      />
    )
  }

  return <path {...props} d={geometry.d} transform={transform} />
}

function CrownAdornment({
  heightScale = 1,
  size
}: {
  heightScale?: number
  size: number
}) {
  return (
    <svg
      aria-hidden
      viewBox='0 0 86 52'
      style={{ height: size * (52 / 86) * heightScale, width: size }}>
      <path
        fill='#FFFFFF'
        d='M0.00645334 17.4495L2.54301 49.9369C2.62429 50.978 3.49275 51.7812 4.53694 51.7812H80.8371C81.8813 51.7812 82.7497 50.978 82.831 49.9369L85.3676 17.4495C85.4973 15.7877 83.6566 14.7057 82.2677 15.6274L62.0142 29.0686C61.0334 29.7195 59.7054 29.3868 59.1473 28.3504L44.448 1.05157C43.6928 -0.350829 41.6812 -0.350833 40.9261 1.05157L26.2267 28.3504C25.6686 29.3868 24.3406 29.7195 23.3598 29.0686L3.1063 15.6274C1.71744 14.7057 -0.123299 15.7877 0.00645334 17.4495Z'
      />
    </svg>
  )
}

function BadgeShape({
  clipPathId,
  shapeId,
  toneId
}: {
  clipPathId: string
  shapeId: BadgeShapeId
  toneId: BadgeToneId
}) {
  const tone = BADGE_TONES[toneId]
  const shape = getBadgeShapeSpec(shapeId)
  const transform = getBadgeShapeTransform(shapeId)

  return (
    <>
      <defs>
        <linearGradient
          id={`${clipPathId}-fill`}
          x1='15%'
          y1='12%'
          x2='85%'
          y2='90%'>
          <stop offset='0%' stopColor={tone.fillStart} />
          <stop offset='100%' stopColor={tone.fillEnd} />
        </linearGradient>
      </defs>
      {renderShapeGeometry({
        geometry: shape.fill,
        props: {
          fill: `url(#${clipPathId}-fill)`
        },
        transform
      })}
    </>
  )
}

function BadgeShapeOutline({
  shapeId,
  stroke,
  strokeWidth
}: {
  shapeId: BadgeShapeId
  stroke: string
  strokeWidth: number
}) {
  const shape = getBadgeShapeSpec(shapeId)
  const transform = getBadgeShapeTransform(shapeId)

  return renderShapeGeometry({
    geometry: shape.outline,
    props: {
      fill: 'none',
      stroke,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      strokeWidth,
      vectorEffect: 'non-scaling-stroke'
    },
    transform
  })
}

function BadgeContent({
  shapeId,
  size,
  symbol,
  toneId
}: {
  shapeId: BadgeShapeId
  size: number
  symbol?: BadgeDisplaySymbol
  toneId: BadgeToneId
}) {
  const tone = BADGE_TONES[toneId]
  const contentConfig = BADGE_CONTENT_CONFIG[shapeId]
  const contentOffsetY = size * contentConfig.offsetYRatio

  if (!symbol) {
    return null
  }

  if (symbol.kind === 'emoji') {
    return (
      <span
        style={{
          fontSize: size * 0.295 * contentConfig.emojiScale,
          lineHeight: 1,
          textShadow: '0 2px 10px rgba(255, 255, 255, 0.7)',
          transform: `translateY(${contentOffsetY}px)`
        }}>
        {symbol.emoji}
      </span>
    )
  }

  const Icon = getUntitledIconComponent(symbol.iconName)
  const bodySize = size * 0.29 * contentConfig.iconScale
  const bodyStrokeWidth = Number(Math.max(2.35, size * 0.0145).toFixed(2))
  const outlineOffset = Number(Math.max(1.1, size * 0.012).toFixed(2))
  const outlineOffsets = [
    [0, outlineOffset],
    [0, -outlineOffset],
    [outlineOffset, 0],
    [-outlineOffset, 0],
    [outlineOffset * 0.72, outlineOffset * 0.72],
    [-outlineOffset * 0.72, outlineOffset * 0.72],
    [outlineOffset * 0.72, -outlineOffset * 0.72],
    [-outlineOffset * 0.72, -outlineOffset * 0.72]
  ] as const

  return (
    <span
      className='relative block'
      style={{ height: bodySize, width: bodySize }}>
      {outlineOffsets.map(([offsetX, offsetY], index) => (
        <Icon
          key={index}
          aria-hidden
          style={{
            color: '#FFFFFF',
            height: bodySize,
            left: '50%',
            position: 'absolute',
            top: '50%',
            transform: `translate(calc(-50% + ${offsetX}px), calc(-50% + ${
              contentOffsetY + offsetY
            }px))`,
            width: bodySize
          }}
          strokeWidth={bodyStrokeWidth}
        />
      ))}
      <Icon
        aria-hidden
        style={{
          color: tone.icon,
          height: bodySize,
          left: '50%',
          position: 'absolute',
          top: '50%',
          transform: `translate(-50%, calc(-50% + ${contentOffsetY}px))`,
          width: bodySize
        }}
        strokeWidth={bodyStrokeWidth}
      />
    </span>
  )
}

export function BadgeDisplay({
  patternId,
  shapeId,
  symbol,
  toneId = 'violet',
  size = 180,
  topAdornment,
  rank,
  label,
  className
}: BadgeDisplayProps) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const clipPathId = `${CLIP_ID_SUFFIX}-${id}`
  const shadowId = `${SHADOW_ID_SUFFIX}-${id}`
  const innerRimShadowId = `${INNER_RIM_SHADOW_ID_SUFFIX}-${id}`
  const tone = BADGE_TONES[toneId]
  const width = Math.round(size * (201 / 224))
  const shouldUseThinBorder = width < 60
  const outerStrokeWidth = shouldUseThinBorder ? 3 : 6
  const innerStrokeWidth = shouldUseThinBorder ? 1.1 : 2.2
  const crownConfig = BADGE_CROWN_CONFIG[shapeId]
  const crownWidth = Math.round(width * crownConfig.widthRatio)
  const crownTop = Math.round(size * crownConfig.topRatio)
  const normalizedRank = formatRankLabel(rank)
  const normalizedLabel = label ? capitalizeFirstCharacter(label) : null
  const captionText = normalizedRank ?? normalizedLabel
  const captionHeight = captionText ? Math.round(size * 0.025) : 0
  const captionFontSize = Math.max(15, size * 0.145)
  const captionOutlineWidth = Math.max(12.19, size * 0.0656)
  const captionBottom = Math.round(size * 0.07)
  const captionBaselineY = size + captionHeight - captionBottom

  return (
    <div className={twMerge('inline-flex flex-col items-center', className)}>
      <div className='relative' style={{ height: size + captionHeight, width }}>
        {topAdornment === 'crown' ? (
          <div
            className='absolute left-1/2 -translate-x-1/2'
            style={{ top: crownTop, zIndex: 0 }}>
            <CrownAdornment
              heightScale={crownConfig.heightScale}
              size={crownWidth}
            />
          </div>
        ) : null}
        <svg
          width={width}
          height={size}
          viewBox={BADGE_VIEW_BOX}
          className='relative z-10 overflow-visible'>
          <defs>
            <clipPath id={clipPathId}>
              {renderShapeGeometry({
                geometry: getBadgeShapeSpec(shapeId).outline,
                transform: getBadgeShapeTransform(shapeId)
              })}
            </clipPath>
            <filter id={shadowId} x='-30%' y='-25%' width='160%' height='170%'>
              <feDropShadow
                dx='0'
                dy='6'
                stdDeviation='6'
                floodColor='rgba(74, 101, 114, 0.16)'
              />
            </filter>
            <filter
              id={innerRimShadowId}
              x='-10%'
              y='-10%'
              width='120%'
              height='120%'>
              <feGaussianBlur stdDeviation='0.8' />
            </filter>
            <radialGradient id={`${id}-glow`} cx='35%' cy='20%' r='85%'>
              <stop offset='0%' stopColor='rgba(255,255,255,0.38)' />
              <stop offset='55%' stopColor='rgba(255,255,255,0.06)' />
              <stop offset='100%' stopColor='rgba(255,255,255,0)' />
            </radialGradient>
          </defs>

          <g filter={`url(#${shadowId})`}>
            <BadgeShape
              clipPathId={clipPathId}
              shapeId={shapeId}
              toneId={toneId}
            />
            <g clipPath={`url(#${clipPathId})`}>
              <BadgeShape
                clipPathId={clipPathId}
                shapeId={shapeId}
                toneId={toneId}
              />
              {patternId ? (
                <BadgePatternLayer
                  color={tone.icon}
                  patternId={patternId}
                  className='h-full w-full'
                />
              ) : null}
              <rect
                x='0'
                y='0'
                width='100'
                height='112'
                fill={`url(#${id}-glow)`}
              />
              <g filter={`url(#${innerRimShadowId})`}>
                <BadgeShapeOutline
                  shapeId={shapeId}
                  stroke={tone.icon}
                  strokeWidth={innerStrokeWidth}
                />
              </g>
            </g>
            <BadgeShapeOutline
              shapeId={shapeId}
              stroke='#FFFFFF'
              strokeWidth={outerStrokeWidth}
            />
          </g>
        </svg>
        <div
          className='pointer-events-none absolute left-0 top-0 z-20 flex items-center justify-center'
          style={{ height: size, width }}>
          <BadgeContent
            shapeId={shapeId}
            size={size}
            symbol={symbol}
            toneId={toneId}
          />
        </div>
        {captionText ? (
          <svg
            aria-hidden
            className='pointer-events-none absolute left-0 top-0 z-30 overflow-visible'
            height={size + captionHeight}
            width={width}>
            <text
              fill='none'
              fontFamily='Inter, sans-serif'
              fontSize={captionFontSize}
              fontWeight={600}
              stroke='#FFFFFF'
              strokeLinejoin='round'
              strokeWidth={captionOutlineWidth}
              style={{ letterSpacing: '-0.04em' }}
              textAnchor='middle'
              x='50%'
              y={captionBaselineY}>
              {captionText}
            </text>
            <text
              fill={tone.icon}
              fontFamily='Inter, sans-serif'
              fontSize={captionFontSize}
              fontWeight={600}
              style={{ letterSpacing: '-0.04em' }}
              textAnchor='middle'
              x='50%'
              y={captionBaselineY}>
              {captionText}
            </text>
          </svg>
        ) : null}
      </div>
    </div>
  )
}
